import { useState, useEffect } from "react";
import {
  Spinner,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Tooltip,
  Dropdown,
  DropdownItem,
  DropdownTrigger,
  DropdownMenu,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalFooter
} from "@heroui/react";
import { getUsers, postUserAction } from "../../api/api";
import { FaSave, FaTrash, FaChevronDown } from "react-icons/fa";
import toast from "react-hot-toast";

const Settings = () => {
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState<any[]>([]);
    const [groupSelections, setGroupSelections] = useState<Record<string, string>>({});

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [newEmail, setNewEmail] = useState("");
    const [newGroup, setNewGroup] = useState("none");

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<any>(null);


  const fetchUserData = async () => {
    setLoading(true);
    try {
      const users = await getUsers();
      setUsers(users);

      const initialSelections: Record<string, string> = {};
      users.forEach((user) => {
        const groups = user.Groups && user.Groups.length > 0 ? user.Groups : ["none"];
        initialSelections[user.Username] = groups[0];
      });
      setGroupSelections(initialSelections);
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleSave = async (user: any) => {
    const selected = groupSelections[user.Username];
    const groups = selected && selected !== "none" ? [selected] : [];

    if (groups.length === 0) {
        groups[0] = ""
    }

    const updatedUser = {
        username: user.email,
        action: "update_group",
        group: groups[0],
    };

    const toastId = toast.loading(`Saving changes for ${user.email}...`);

    try {
      await postUserAction(updatedUser);
      toast.success(`${user.email} updated successfully.`, { id: toastId });
      fetchUserData(); // Refresh user list
    } catch (error) {
      console.error("Save failed:", error);
      toast.error(`Failed to update ${user.email}`, { id: toastId });
    }
  };

  const handleGroupChange = (username: string, group: string) => {
    setGroupSelections((prev) => ({
      ...prev,
      [username]: group,
    }));
  };

  const groupOptions = [
    { key: "Administrators", label: "Administrators" },
    { key: "Staff", label: "Staff" },
    { key: "Volunteer", label: "Volunteer" },
  ];

  return (
    <main className={`flex flex-col p-4 ${loading ? "opacity-50 pointer-events-none" : ""}`}>
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-[9998]">
          <Spinner size="lg" color="primary" />
        </div>
      )}
        <div className="max-w-screen-lg w-full mx-auto">
        {/* 🔽 Create button row added here */}
        <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">User Management</h1>
        <Button color="primary" onClick={() => setIsCreateOpen(true)}>
            Add User
        </Button>
        </div>
        <Table aria-label="Users Table" removeWrapper>
          <TableHeader>
            <TableColumn>Email</TableColumn>
            <TableColumn>Status</TableColumn>
            <TableColumn>Group</TableColumn>
            <TableColumn>Actions</TableColumn>
          </TableHeader>
          <TableBody>
            {users.map((user) => {
              const selectedKey = groupSelections[user.Username] || "none";
              const selectedLabel = groupOptions.find((g) => g.key === selectedKey)?.label || "None";

              return (
                <TableRow key={user.Username}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.UserStatus}</TableCell>
                  <TableCell>
                    <Dropdown>
                      <DropdownTrigger>
                        <Button
                          variant="bordered"
                          className="w-[200px] flex justify-between items-center"
                        >
                          {selectedLabel}
                          <FaChevronDown  className="ml-2" />
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu
                        aria-label="Group Selection"
                        onAction={(key) => handleGroupChange(user.Username, key as string)}
                      >
                        {groupOptions.map((group) => (
                          <DropdownItem key={group.key}>{group.label}</DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Tooltip content="Save User">
                        <Button
                          isIconOnly
                          variant="light"
                          color="primary"
                          onClick={() => handleSave(user)}
                        >
                          <FaSave size={22}/>
                        </Button>
                      </Tooltip>
                      <Tooltip content="Delete User">
                        <Button
                            isIconOnly
                            variant="light"
                            color="danger"
                            onClick={() => {
                                setUserToDelete(user);
                                setIsDeleteOpen(true);
                            }}
                            >
                            <FaTrash size={22}/>
                        </Button>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Modal 
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          classNames={{ closeButton: "bg-gray-100" }}
        >
            <ModalContent>
                <ModalHeader>Create New User</ModalHeader>
                <ModalBody>
                <div className="flex flex-col gap-4">
                    <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="w-full border rounded px-3 py-2 text-sm"
                        placeholder="user@example.com"
                    />
                    </div>

                    <div>
                    <label className="block text-sm font-medium mb-1">Group</label>
                    <Dropdown>
                        <DropdownTrigger>
                        <Button variant="bordered" className="w-full flex justify-between">
                            {groupOptions.find((g) => g.key === newGroup)?.label || "Select Group"}
                            <FaChevronDown className="ml-2" />
                        </Button>
                        </DropdownTrigger>
                        <DropdownMenu
                        aria-label="Select Group"
                        onAction={(key) => setNewGroup(key as string)}
                        >
                        {groupOptions.map((group) => (
                            <DropdownItem key={group.key}>{group.label}</DropdownItem>
                        ))}
                        </DropdownMenu>
                    </Dropdown>
                    </div>
                </div>
                </ModalBody>
                <ModalFooter>
                <Button variant="light" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={async () => {
                    if (!newEmail) {
                        toast.error("Please enter a valid email");
                        return;
                    }

                    const toastId = toast.loading("Creating user...");
                    try {
                        await postUserAction({
                        username: newEmail,
                        action: "create_user",
                        group: newGroup !== "none" ? newGroup : "",
                        });

                        toast.success("User created successfully", { id: toastId });
                        setIsCreateOpen(false);
                        setNewEmail("");
                        setNewGroup("none");
                        fetchUserData();
                    } catch (err) {
                        console.error(err);
                        toast.error("Failed to create user", { id: toastId });
                    }
                    }}
                >
                    Create
                </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
        <Modal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)}>
            <ModalContent>
                <ModalHeader>Confirm Delete</ModalHeader>
                <ModalBody>
                <p>Are you sure you want to delete:</p>
                <p><strong><center>{userToDelete?.email}</center></strong></p>
                </ModalBody>
                <ModalFooter>
                <Button variant="light" onClick={() => setIsDeleteOpen(false)}>
                    Cancel
                </Button>
                <Button
                    color="danger"
                    onClick={async () => {
                    if (!userToDelete) return;
                    const toastId = toast.loading(`Deleting ${userToDelete.email}...`);

                    try {
                        await postUserAction({
                        username: userToDelete.email,
                        action: "delete_user",
                        });

                        toast.success(`${userToDelete.email} deleted successfully.`, { id: toastId });
                        setIsDeleteOpen(false);
                        setUserToDelete(null);
                        fetchUserData();
                    } catch (error) {
                        console.error("Delete failed:", error);
                        toast.error(`Failed to delete ${userToDelete.email}`, { id: toastId });
                    }
                    }}
                >
                    Yes, Delete
                </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
        </div>
    </main>
  );
};

export default Settings;
